import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import get from 'lodash.get';
import { I18n } from 'react-redux-i18n';
import { AnalyticsLineChart } from '../../../components/charts';
import { summaryTypeToFormatter } from '../../../components/charts/metrics';
import { Record as RecordHelper } from '../../../modules/bin/utility';
import { isArrayWithItems } from '../../../helpers/arrayHelper';
import * as dashboardActions from '../reducer';
import { getTimePeriod } from '../../../helpers/dateAndTimeHelper';
import { getDashboardCreatedAt } from '../helper';
import { useParams } from 'react-router-dom';

type Props = {
  summaryType: string;
  summaryDataRecord: any;
  reportByDateAndMetricType: string;
  configurations: any;
  timeBracket: string;
  timeLapse: string;
  timeLapseFrom: string | null;
  timeLapseTo: string | null;
  timePeriod: { dateFrom?: string | null; dateTo?: string | null } | null;
  modelRecord: any;
  getSummaryDataRecord: (request: any) => void;
};

const DashboardChart: React.FC<Props> = ({ summaryType, summaryDataRecord, reportByDateAndMetricType: reportUnit, configurations, timeBracket, timeLapse, timeLapseFrom, timeLapseTo, timePeriod, modelRecord, getSummaryDataRecord }) => {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const isLoading = !RecordHelper.isRecordReady(summaryDataRecord);
  const list = (get(summaryDataRecord, 'data.data.list') || []) as Array<any>;

  // Kick off data load if not started (old chart used to do this)
  useEffect(() => {
    const notStarted = RecordHelper.notStarted?.(summaryDataRecord) || !summaryDataRecord;
    const loading = RecordHelper.isRecordLoading?.(summaryDataRecord);
    if (!id || !configurations) return;
    if (notStarted && !loading) {
      const predefined = timePeriod || ({} as any);
      const hasPredefined = !!(predefined?.dateFrom || predefined?.dateTo);
      const entity = configurations?.entity;
      const createdAtLocal = getDashboardCreatedAt(entity, modelRecord?.data?.data);
      const computedPeriod = getTimePeriod(
        createdAtLocal,
        reportUnit,
        timeLapse,
        timeLapseFrom as any,
        timeLapseTo as any
      );
      const periodToUse = hasPredefined ? predefined : computedPeriod;

      const request = dashboardActions.getSummaryDataRecordRequest(
        id,
        configurations.baseFilter,
        periodToUse,
        timeBracket,
        reportUnit
      );
      // eslint-disable-next-line no-console
      console.debug('[DashboardChart] Dispatch getSummaryDataRecord', {
        id,
        baseFilter: configurations.baseFilter,
        periodToUse,
        timeBracket,
        reportUnit
      });
      getSummaryDataRecord(request);
    }
  }, [id, configurations, summaryDataRecord, reportUnit, timeBracket, timeLapse, timeLapseFrom, timeLapseTo, timePeriod, modelRecord, getSummaryDataRecord]);

  const granularity = useMemo(() => {
    switch (reportUnit) {
      case 'Daily':
        return 'daily' as const;
      case 'Weekly':
        return 'weekly' as const;
      case 'Monthly':
        return 'monthly' as const;
      default:
        return 'daily' as const;
    }
  }, [reportUnit]);

  const series = useMemo(() => {
    if (!isArrayWithItems(list)) return [];
    const valueKey = summaryType;
    const formatter = summaryTypeToFormatter[valueKey] || 'integer';
    const points = list
      .map((row) => ({
        date: row.date,
        value: row[valueKey] || 0,
        currency: row.currency
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const last = points[points.length - 1];
    // eslint-disable-next-line no-console
    console.debug('[DashboardChart] series mapped', {
      summaryType: valueKey,
      count: points.length,
      last
    });
    return [
      {
        id: 'primary',
        name: I18n.t('dashboard.top-area.chart.selected', { defaultValue: 'Selected' }),
        color: getComputedStyle(document.body).getPropertyValue('--gr-orange')?.trim() || '#ff6b35',
        data: points,
        valueFormatter: formatter
      }
    ];
  }, [list, summaryType]);

  return (
    <AnalyticsLineChart
      series={series as any}
      isLoading={isLoading}
      noDataMessage={I18n.t('dashboard.top-area.chart.no-items')}
      legend={{ show: false }}
      headerSummary={{ show: false, mode: 'single' }}
      time={{ alignMode: 'absolute', granularity, autoAggregate: false, aggregationThresholdDays: 180, axisType: 'date' }}
      performance={{ bullets: 'auto', maxSeries: 5, tensionX: 0.8, tensionY: 0.8 }}
      ariaLabel={I18n.t('dashboard.top-area.chart.aria', { defaultValue: 'Dashboard metric chart' })}
      height={360}
    />
  );
};

const mapState = ({ dashboard }: any) => ({
  summaryType: dashboard.summaryType,
  summaryDataRecord: dashboard.summaryDataRecord,
  reportByDateAndMetricType: dashboard.reportByDateAndMetricType,
  configurations: dashboard.configurations,
  timeBracket: dashboard.timeBracket,
  timeLapse: dashboard.timeLapse,
  timeLapseFrom: dashboard.timeLapseFrom,
  timeLapseTo: dashboard.timeLapseTo,
  timePeriod: dashboard.timePeriod,
  modelRecord: dashboard.modelRecord
});
const mapDispatch = (dispatch: any) => ({
  getSummaryDataRecord: (request: any) => dispatch(dashboardActions.getSummaryDataRecord(request))
});

export default connect(mapState, mapDispatch)(DashboardChart);
